"use client";

/**
 * ChatbotIndicator Component
 *
 * Shows which chatbot configuration is active for the current thread.
 * Displays in the chat header area.
 */
import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { Chatbot } from "@/types/chatbot";
type ChatbotIndicatorProps = {
  /** The active chatbot for the current thread */
  chatbot: Chatbot | null;
  /** Called when the user wants to change the chatbot */
  onChangeChatbot?: () => void;
  /** Whether the indicator is clickable */
  clickable?: boolean;
  /** Compact mode */
  compact?: boolean;
  /** Additional class names */
  className?: string;
};
export function ChatbotIndicator(t0) {
  const $ = _c(26);
  const {
    chatbot,
    onChangeChatbot,
    clickable: t1,
    compact: t2,
    className: t3
  } = t0;
  const clickable = t1 === undefined ? true : t1;
  const compact = t2 === undefined ? false : t2;
  const className = t3 === undefined ? "" : t3;
  if (!chatbot) {
    return null;
  }
  const provider = chatbot.config.model?.provider || "ai";
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      openai: {
        bg: "bg-green-500/10",
        text: "text-green-400",
        ring: "ring-green-500/20"
      },
      claude: {
        bg: "bg-orange-500/10",
        text: "text-orange-400",
        ring: "ring-orange-500/20"
      },
      grok: {
        bg: "bg-blue-500/10",
        text: "text-blue-400",
        ring: "ring-blue-500/20"
      },
      gemini: {
        bg: "bg-purple-500/10",
        text: "text-purple-400",
        ring: "ring-purple-500/20"
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const providerColors = t4;
  let t5;
  if ($[1] !== provider) {
    t5 = providerColors[provider] || {
      bg: "bg-slate-500/10",
      text: "text-slate-400",
      ring: "ring-slate-500/20"
    };
    $[1] = provider;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const colors = t5;
  const t6 = `h-3.5 w-3.5 flex-shrink-0 ${colors.text}`;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== t6) {
    t8 = <svg className={t6} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t7}</svg>;
    $[4] = t6;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = `font-medium truncate ${compact ? "max-w-[80px]" : "max-w-[150px]"}`;
  let t10;
  if ($[6] !== chatbot.name || $[7] !== t9) {
    t10 = <span className={t9}>{chatbot.name}</span>;
    $[6] = chatbot.name;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== clickable || $[10] !== colors.text || $[11] !== onChangeChatbot) {
    t11 = clickable && onChangeChatbot && <svg className={`h-3 w-3 flex-shrink-0 ${colors.text} opacity-60`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
    $[9] = clickable;
    $[10] = colors.text;
    $[11] = onChangeChatbot;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t11 || $[15] !== t8) {
    t12 = <>{t8}{t10}{t11}</>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t8;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const content = t12;
  const baseClasses = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 ring-inset ${colors.bg} ${colors.text} ${colors.ring} ${className}`;
  if (clickable && onChangeChatbot) {
    const t13 = `${baseClasses} hover:opacity-80 transition-opacity cursor-pointer`;
    const t14 = `Using ${chatbot.name} configuration. Click to change.`;
    let t15;
    if ($[17] !== content || $[18] !== onChangeChatbot || $[19] !== t13 || $[20] !== t14) {
      t15 = <button type="button" onClick={onChangeChatbot} className={t13} title={t14}>{content}</button>;
      $[17] = content;
      $[18] = onChangeChatbot;
      $[19] = t13;
      $[20] = t14;
      $[21] = t15;
    } else {
      t15 = $[21];
    }
    return t15;
  }
  const t13 = `Using ${chatbot.name} configuration`;
  let t14;
  if ($[22] !== baseClasses || $[23] !== content || $[24] !== t13) {
    t14 = <span className={baseClasses} title={t13}>{content}</span>;
    $[22] = baseClasses;
    $[23] = content;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
}